import { Skeleton } from '@mui/material';
import { isEqual, set } from 'lodash';
import { lazy, memo, Suspense } from 'react';
import { useCleanUpDefaultFonts } from 'src/hooks/useCleanUpDefaultFonts';
import { ComponentTypes } from 'src/interfaces';

import { GCFErrorBoundary } from '../errors/GCFErrorBoundary';
import { GCFConfigProvider } from '../gcfConfigProvider';
import { GcfComponentSectionProps } from './GcfComponentSection.interface';
import { GcfComponentSectionWrapper } from './GcfComponentSectionWrapper';

const Section = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.Section,
  })),
);

const MemorizedSection = memo(
  (props: any) => {
    return <Section {...props} />;
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);

export const GcfComponentSection = memo(
  ({
    screenSectionId,
    assemblyComponents,
    height,
    width,
    scale,
    previewData,
    containerSize,
    fullWidth,
    containerStyle,
    overrideQuerySetting,
    hidden,
    isFromSidebar,
    productFilters,
    productVariationFilters,
    skipProductsApiOnEmptyFilters,
    dimPreview,
  }: GcfComponentSectionProps) => {
    useCleanUpDefaultFonts();

    const updatedAssemblyComponents = assemblyComponents.map((data) => {
      if (!data?.component?.theme) {
        set(
          data,
          'component.theme',
          data.component?.config?.scaleTypeKey ??
            data.component?.config?.themeKey ??
            'default',
        );
      }

      return data;
    });

    return (
      <GcfComponentSectionWrapper
        hidden={hidden}
        height={height}
        width={width}
        scale={scale}
        fullWidth={fullWidth}
        containerSize={containerSize}
        containerStyle={containerStyle}
        dimPreview={dimPreview}
        overflow={assemblyComponents.some(
          (item) => item.component?.type === ComponentTypes.COD,
        )}>
        <GCFErrorBoundary>
          <Suspense
            fallback={
              <Skeleton height="100%" width="100%" variant="rectangular" />
            }>
            <GCFConfigProvider
              screenSectionId={screenSectionId}
              overrideQuerySetting={overrideQuerySetting}
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}>
              <MemorizedSection
                id={screenSectionId}
                top="0"
                left="0"
                height={height}
                width={width}
                previewData={previewData}
                assemblyComponents={updatedAssemblyComponents}
                isFromSidebar={isFromSidebar}
              />
            </GCFConfigProvider>
          </Suspense>
        </GCFErrorBoundary>
      </GcfComponentSectionWrapper>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);
